import { useState, useEffect, lazy, Suspense, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { LanguageProvider, useLanguage } from "@/contexts/LanguageContext";
import { GuideProvider } from "@/components/guide";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { AuthProvider } from "@/hooks/useAuth";
import { DemoProvider } from "@/contexts/DemoContext";
import { useManifestSwitcher } from "@/hooks/useManifestSwitcher";
import { useAnalyticsTracking } from "@/hooks/useAnalyticsTracking";
import { VersionGate } from "@/components/VersionGate";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ConsentReconfirmGate } from "@/components/auth/ConsentReconfirmGate";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { LegacyPrefixRedirect } from "@/components/LegacyRedirect";
import { PermissionGuard } from "@/components/guards/PermissionGuard";
import { RoleGate } from "@/components/auth/RoleGate";
import { SystemRole } from "@/lib/rbac";
import { portalPathForRole } from "@/lib/authHelpers";
import { AppLayout } from "@/components/layout";
import { ImpersonationBanner } from "@/components/super-admin/ImpersonationBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Loader2 } from "lucide-react";
import { isTransientNetworkError, isJWTClockSkewError } from "@/lib/networkErrors";
import { queryCacheOnError, mutationCacheOnError } from "@/lib/queryCacheGuards";
import { lazyWithReload } from "@/lib/lazyWithReload";
import { supabase } from "@/integrations/supabase/client";
import { nestRefreshSession } from "@/services/api";
import { readNestRefreshToken, persistNestRefreshToken } from "@/lib/authHelpers";

// Eager: auth & shell (needed immediately)
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import NoAccess from "./pages/NoAccess";

// Lazy: dashboard pages
const GoRedirect = lazy(() => import("./pages/GoRedirect"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Properties = lazy(() => import("./pages/Properties"));
// ONE Opportunities surface for staff + owners (permission-aware Ads-style
// pages, S6): mounted here under /dashboard and in the owner portal.
const Opportunities = lazy(() => import("./pages/portal/owner/OwnerInvestmentOpportunities"));
const OpportunityDetails = lazy(() => import("./pages/portal/owner/OwnerOpportunityDetails"));
const OpportunityUnitDetails = lazy(() => import("./pages/portal/owner/OwnerOpportunityUnitDetails"));
const PropertyDetails = lazy(() => import("./pages/PropertyDetails"));
const UnitDetails = lazy(() => import("./pages/UnitDetails"));
const Ads = lazy(() => import("./pages/Ads"));
const AdDetails = lazy(() => import("./pages/AdDetails"));
const ContractRequests = lazy(() => import("./pages/ContractRequests"));
const Contracts = lazy(() => import("./pages/Contracts"));
const ContractDetails = lazy(() => import("./pages/ContractDetails"));
const LeadsAddressBook = lazy(() => import("./pages/dashboard/leads/LeadsAddressBook"));
const LeadDetails = lazy(() => import("./pages/dashboard/leads/LeadDetails"));
const Finance = lazy(() => import("./pages/Finance"));
const Reports = lazy(() => import("./pages/Reports"));
const Profile = lazy(() => import("./pages/Profile"));
const Settings = lazy(() => import("./pages/Settings"));
const MySubscription = lazy(() => import("./pages/MySubscription"));
const RerVerifications = lazy(() => import("./pages/RerVerifications"));
const RegaDashboard = lazy(() => import("./pages/RegaDashboard"));
const TenantDetails = lazy(() => import("./pages/TenantDetails"));
const OwnerDetails = lazy(() => import("./pages/OwnerDetails"));
const OwnerSignupEntry = lazy(() =>
  import("./pages/onboarding/OnboardingEntry").then((m) => ({ default: m.OwnerSignupEntry })),
);
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ReferralLanding = lazy(() => import("./pages/ReferralLanding"));
const Maintenance = lazy(() => import("./pages/Maintenance"));
const MaintenanceRequestDetails = lazy(() => import("./pages/MaintenanceRequestDetails"));
const Employees = lazy(() => import("./pages/Employees"));
const ReferralsReport = lazy(() => import("./pages/ReferralsReport"));
const MyReferrals = lazy(() => import("./pages/portal/employee/EmployeeReferrals"));
const MediaReview = lazy(() => import("./pages/MediaReview"));
const KycCompliance = lazy(() => import("./pages/KycCompliance"));
const EmployeeDetails = lazy(() => import("./pages/EmployeeDetails"));
const NotificationsCenter = lazy(() => import("./pages/NotificationsCenter"));
const MessagesPage = lazy(() => import("./pages/dashboard/MessagesPage"));
const ConversationsPage = lazy(() => import("./pages/dashboard/ConversationsPage"));
const RealEstateRequests = lazy(() => import("./pages/RealEstateRequests"));
const RealEstateRequestDetails = lazy(() => import("./pages/RealEstateRequestDetails"));
const MasterCardDetail = lazy(() => import("./pages/MasterCardDetail"));
const MasterCardUnitDetail = lazy(() => import("./pages/MasterCardUnitDetail"));
const MasterCardsList = lazy(() => import("./pages/MasterCardsList"));

// Lazy: finance detail pages
const InvoiceDetails = lazy(() => import("./pages/finance/InvoiceDetails"));
const ReceiptDetails = lazy(() => import("./pages/finance/ReceiptDetails"));
const ExpenseDetails = lazy(() => import("./pages/finance/ExpenseDetails"));
const PaymentVerifications = lazy(() => import("./pages/finance/PaymentVerifications"));

// Lazy: report pages
const RevenueReport = lazy(() => import("./pages/reports/RevenueReport"));
const CollectionsReport = lazy(() => import("./pages/reports/CollectionsReport"));
const OperatingExpensesReport = lazy(() => import("./pages/reports/OperatingExpensesReport"));
const MaintenanceReports = lazy(() => import("./pages/reports/MaintenanceReports"));
const AccountsReceivableReport = lazy(() => import("./pages/reports/AccountsReceivableReport"));
const PropertyPerformanceReport = lazy(() => import("./pages/reports/PropertyPerformanceReport"));
const OccupancyReports = lazy(() => import("./pages/reports/OccupancyReports"));
const FinancialReports = lazy(() => import("./pages/reports/FinancialReports"));
const InvoiceReport = lazy(() => import("./pages/reports/InvoiceReport"));
const IncomeStatementReport = lazy(() => import("./pages/reports/IncomeStatementReport"));
const TenantsReport = lazy(() => import("./pages/reports/TenantsReport"));
const BalanceSheetReport = lazy(() => import("./pages/reports/BalanceSheetReport"));
const CashFlowReport = lazy(() => import("./pages/reports/CashFlowReport"));

// Lazy: public pages (with auto-reload on stale chunks)
const InviteEntry = lazyWithReload(() =>
  import("./pages/onboarding/OnboardingEntry").then((m) => ({ default: m.InviteEntry })),
);
const RequestService = lazyWithReload(() => import("./pages/RequestService"));
const Services = lazyWithReload(() => import("./pages/Services"));
const InstallApp = lazyWithReload(() => import("./pages/InstallApp"));
const CustomerRating = lazyWithReload(() => import("./pages/CustomerRating"));
const VisitSchedule = lazyWithReload(() => import("./pages/VisitSchedule"));
const InspectionReport = lazyWithReload(() => import("./pages/InspectionReport"));

// Lazy: portals
const OwnerPortal = lazy(() => import("./pages/portal/owner"));
const TenantPortal = lazy(() => import("./pages/portal/tenant"));
const EmployeePortal = lazy(() => import("./pages/portal/employee"));
const ServiceProviderPortal = lazy(() => import("./pages/portal/provider"));

// Lazy: brokerage pages (Inc-2: wizard host; Inc-3: list/detail/edit)
const NewBrokerageContract = lazy(() => import("./pages/brokerage/NewBrokerageContract"));
const BrokerageContractsList = lazy(() => import("./pages/dashboard/brokerage/BrokerageContractsList"));
const BrokerageContractDetail = lazy(() => import("./pages/dashboard/brokerage/BrokerageContractDetail"));
const EditBrokerageContract = lazy(() => import("./pages/dashboard/brokerage/EditBrokerageContract"));

// Lazy: super admin, admin invite
const SuperAdminPortal = lazy(() => import("./pages/super-admin"));
const TestHyperPay = lazy(() => import("./pages/TestHyperPay"));
const AdminInviteRoutes = lazy(() => import("./pages/admin-invite"));
const OwnerImportDemo = lazy(() => import("./pages/OwnerImportDemo"));

// Lazy: invoice payment flow (public, no auth)
const InvoiceCheckout = lazy(() => import("./pages/pay/InvoiceCheckout"));
const InvoicePaymentCallback = lazy(() => import("./pages/pay/InvoicePaymentCallback"));
const MasterInvoiceCheckout = lazy(() => import("./pages/pay/MasterInvoiceCheckout"));
const MasterInvoicePaymentCallback = lazy(() => import("./pages/pay/MasterInvoicePaymentCallback"));

// Lazy: public invoice view (no auth)
const InvoicePublicView = lazyWithReload(() => import("./pages/invoice/InvoicePublicView"));
const QuotationPublicView = lazyWithReload(() => import("./pages/quotation/QuotationPublicView"));
const MasterInvoicePublicView = lazyWithReload(() => import("./pages/invoice/MasterInvoicePublicView"));

// Lazy: public receipt view (no auth — UUID is the access token)
const ReceiptPublicView = lazyWithReload(() => import("./pages/receipt/ReceiptPublicView"));

// Lazy: public task report view (no auth — UUID is the access token)
const TaskReportPublicView = lazyWithReload(() => import("./pages/task-report/TaskReportPublicView"));

// Lazy: landing pages (with auto-reload on stale chunks)
const LandingHome = lazyWithReload(() => import("./pages/landing/LandingHome"));
const LandingAboutUs = lazyWithReload(() => import("./pages/landing/LandingAboutUs"));
const LandingServices = lazyWithReload(() => import("./pages/landing/LandingServices"));
const LandingAdvertisements = lazyWithReload(() => import("./pages/landing/LandingAdvertisements"));
const LandingSelfManagement = lazyWithReload(() => import("./pages/landing/LandingSelfManagement"));
const LandingPropertyDetails = lazyWithReload(() => import("./pages/landing/PropertyDetails"));
// LandingProjectDetails (pages/landing/ProjectDetails.tsx) is GATED, not routed:
// opportunities are owner-exclusive since CEO 2026-07-03 (slice S6). The public
// detail route below redirects to /advertisements. Re-opening seam: restore the
// lazy import + route element (and ungate ProjectsPublicController in apps/api).
const LandingBlog = lazyWithReload(() => import("./pages/landing/LandingBlog"));
const LandingBlogPost = lazyWithReload(() => import("./pages/landing/LandingBlogPost"));
const LandingContentPage = lazyWithReload(() => import("./pages/landing/LandingContentPage"));
const LandingNotFound = lazyWithReload(() => import("./pages/landing/LandingNotFound"));
const PropertyPublicPage = lazyWithReload(() => import("./pages/public/PropertyPublicPage"));

// Track when we last attempted a global JWT refresh to prevent rapid-fire refresh attempts
let lastJWTRefreshAttempt = 0;
const JWT_REFRESH_COOLDOWN = 2000; // 2 seconds between refresh attempts

/**
 * Async retry handler that supports JWT clock skew recovery.
 * Cast to unknown to satisfy react-query's synchronous RetryValue type —
 * in practice react-query awaits the returned Promise correctly at runtime.
 */
const asyncRetryHandler = async (failureCount: number, error: Error): Promise<boolean> => {
  // Handle JWT clock skew errors (PGRST303) with session refresh
  if (isJWTClockSkewError(error)) {
    console.warn('[JWT Clock Skew] PGRST303 detected, refreshing session...', error);

    // Only retry once per query (on first failure)
    if (failureCount > 0) {
      if (import.meta.env.DEV) console.log('[JWT Clock Skew] Already retried this query, failing');
      return false;
    }

    // Check cooldown to prevent rapid-fire refresh attempts across multiple queries
    const now = Date.now();
    if (now - lastJWTRefreshAttempt < JWT_REFRESH_COOLDOWN) {
      if (import.meta.env.DEV) console.log('[JWT Clock Skew] Refresh cooldown active, waiting for existing refresh');
      // Wait for cooldown, then retry (another query may have refreshed the session)
      await new Promise(resolve => setTimeout(resolve, JWT_REFRESH_COOLDOWN));
      return true; // Retry with potentially refreshed session
    }

    lastJWTRefreshAttempt = now;

    try {
      // D4 repoint: Nest refresh (replaces GoTrue refreshSession). Read the stored
      // session once — it provides both the refresh-token fallback (when no Nest
      // token is cached) and the access token we forward, so a legacy session whose
      // refresh token has no nest_sessions row is reissued server-side instead of
      // 401ing on the clock-skew path too (DALLALI-FRONTEND-AQ) — matching useAuth.
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const refreshToken = readNestRefreshToken() ?? currentSession?.refresh_token ?? null;
      if (!refreshToken) {
        console.error('[JWT Clock Skew] No refresh token available for Nest refresh');
        return false;
      }

      const tokens = await nestRefreshSession(refreshToken, currentSession?.access_token);

      const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
      });
      if (sessionError || !sessionData.session) {
        console.error('[JWT Clock Skew] setSession after Nest refresh failed:', sessionError?.message);
        return false;
      }
      
      // Cache the Nest refresh token so useAuth can recover/rotate the session.
      persistNestRefreshToken(tokens.refresh_token);
      
      if (import.meta.env.DEV) console.log('[JWT Clock Skew] Session refreshed via Nest, retrying query');
      // Wait a brief moment for the new session to propagate
      await new Promise(resolve => setTimeout(resolve, 100));
      return true; // Retry the query with fresh JWT
    } catch (refreshErr) {
      console.error('[JWT Clock Skew] Session refresh exception:', refreshErr);
    }

    return false; // Don't retry if refresh failed
  }

  // Don't retry non-network errors
  if (!isTransientNetworkError(error)) {
    return false;
  }
  // Retry network errors up to 3 times
  return failureCount < 3;
};

// Configure QueryClient with Sentry error tracking and retry logic
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min — prevent aggressive refetches that reset form state
      refetchOnWindowFocus: false, // don't refetch when user tabs back mid-form
      // Retry network errors up to 3 times with exponential backoff
      retry: asyncRetryHandler as unknown as boolean,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
  // The guard chains live in src/lib/queryCacheGuards.ts — extracted so their
  // order and coverage are pinned by src/lib/__tests__/queryCacheGuards.test.ts.
  queryCache: new QueryCache({ onError: queryCacheOnError }),
  mutationCache: new MutationCache({ onError: mutationCacheOnError }),
});

// Use Sentry's enhanced BrowserRouter
const SentryRoutes = Sentry.withSentryRouting(Routes);

// Component to switch PWA manifest based on user role
function ManifestSwitcher() {
  useManifestSwitcher();
  return null;
}

// First-party pageview tracking on every route change
function AnalyticsTracker() {
  useAnalyticsTracking();
  return null;
}

// Wrapper component to handle impersonation banner spacing
/** Show a bilingual success/error toast and redirect after email verification */
function EmailVerifiedHandler() {
  const emailNavigate = useNavigate();
  const { t } = useLanguage();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("email-verified") !== "true") return;

    // Check for GoTrue error in hash (failed/expired link includes #error=...&error_description=...)
    const hashParams = new URLSearchParams(window.location.hash.replace("#", ""));
    const error = hashParams.get("error_description") || hashParams.get("error");

    if (error) {
      import("sonner").then(({ toast }) => {
        toast.error(t("رابط التحقق غير صالح أو تم استخدامه بالفعل", "Verification link is invalid or has already been used"));
      });
    } else {
      import("sonner").then(({ toast }) => {
        toast.success(t("تم تأكيد البريد الإلكتروني بنجاح!", "Email verified successfully!"));
      });
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      emailNavigate(session ? "/dashboard/profile" : "/auth", { replace: true });
    });
  }, [emailNavigate, t]);
  return null;
}

function ImpersonationContentWrapper({ children }: { children: ReactNode }) {
  // Access localStorage directly to avoid context dependency issues at App level
  const [hasImpersonation, setHasImpersonation] = useState(false);
  
  useEffect(() => {
    const checkImpersonation = () => {
      const stored = localStorage.getItem("impersonation_session");
      setHasImpersonation(!!stored);
    };
    
    checkImpersonation();
    window.addEventListener('impersonation-changed', checkImpersonation);
    window.addEventListener('storage', checkImpersonation);
    
    return () => {
      window.removeEventListener('impersonation-changed', checkImpersonation);
      window.removeEventListener('storage', checkImpersonation);
    };
  }, []);
  
  return (
    <div className={hasImpersonation ? "pt-12" : ""}>
      {children}
    </div>
  );
}

const App = () => (
  <ErrorBoundary>
    <BrowserRouter
      basename={import.meta.env.BASE_URL}
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <LanguageProvider>
            <GuideProvider>
            <AuthProvider>
              <ImpersonationProvider>
                <DemoProvider>
                <ManifestSwitcher />
                <AnalyticsTracker />
                <ScrollToTop />
                <VersionGate />
                <ConsentReconfirmGate />
                <EmailVerifiedHandler />
                <ImpersonationBanner />
                <ImpersonationContentWrapper>
                <TooltipProvider>
                  <Sonner />
              <Suspense fallback={<div className="flex items-center justify-center h-screen"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
              <SentryRoutes>
              {/* Public Landing Pages */}
              <Route path="/" element={<LandingHome />} />
              <Route path="/home" element={<Navigate to="/" replace />} />
              <Route path="/about-us" element={<LandingAboutUs />} />
              <Route path="/our-services" element={<LandingServices />} />
              <Route path="/advertisements" element={<LandingAdvertisements />} />
              {/* Listing and project pages live at slug URLs (src/lib/localeRoutes.ts);
                  the old UUID paths stay routed and redirect to them. */}
              <Route path="/advertisements/:id" element={<LandingPropertyDetails />} />
              <Route path="/advertisements/property/:id" element={<LandingPropertyDetails />} />
              <Route path="/projects/:id" element={<PropertyPublicPage />} />
              <Route path="/p/:id" element={<PropertyPublicPage />} />
              {/* Opportunities (projects) are owner-exclusive (S6) — old public
                  detail links land back on the listings page. Owners browse
                  opportunities at /portal/owner/opportunities. */}
              <Route path="/advertisements/project/:id" element={<Navigate to="/advertisements" replace />} />
              <Route path="/self-management" element={<LandingSelfManagement />} />
              <Route path="/blog" element={<LandingBlog />} />
              <Route path="/blog/:slug" element={<LandingBlogPost />} />
              <Route path="/privacy-policy" element={<LandingContentPage slug="privacy-policy" />} />
              <Route path="/terms" element={<LandingContentPage slug="terms" />} />
              {/* English versions of the marketing pages — the URL sets the
                  language (src/lib/localeRoutes.ts MARKETING_PATHS). */}
              <Route path="/en" element={<LandingHome />} />
              <Route path="/en/about-us" element={<LandingAboutUs />} />
              <Route path="/en/our-services" element={<LandingServices />} />
              <Route path="/en/advertisements" element={<LandingAdvertisements />} />
              <Route path="/en/advertisements/:id" element={<LandingPropertyDetails />} />
              <Route path="/en/advertisements/property/:id" element={<LandingPropertyDetails />} />
              <Route path="/en/projects/:id" element={<PropertyPublicPage />} />
              <Route path="/en/p/:id" element={<PropertyPublicPage />} />
              <Route path="/en/self-management" element={<LandingSelfManagement />} />
              <Route path="/en/blog" element={<LandingBlog />} />
              <Route path="/en/blog/:slug" element={<LandingBlogPost />} />
              <Route path="/en/privacy-policy" element={<LandingContentPage slug="privacy-policy" />} />
              <Route path="/en/terms" element={<LandingContentPage slug="terms" />} />
              <Route path="/request-service" element={<RequestService />} />
              <Route path="/services" element={<Services />} />
              <Route path="/visit-schedule/:token" element={<VisitSchedule />} />
              <Route path="/inspection-report/:taskId" element={<InspectionReport />} />
              <Route path="/install" element={<InstallApp />} />
              <Route path="/rate" element={<CustomerRating />} />
              {/* Stable deep-link resolver for WhatsApp templates (immutable
                  once approved) — redirects /go/<key>/<id> to the current route.
                  See src/lib/goLinks.ts. */}
              <Route path="/go/:key/:id" element={<GoRedirect />} />
              <Route path="/go/:key" element={<GoRedirect />} />
              
              {/* Auth Routes */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              {/* Owner sign-up: unified onboarding while `kyc_onboarding` is ON, legacy OwnerAuth otherwise. */}
              <Route path="/owner-auth" element={<OwnerSignupEntry />} />
              {/* Public employee-referral landing — resolves the code, stashes it,
                  and hands off into the owner-signup flow. */}
              <Route path="/r/:code" element={<ReferralLanding />} />
              {/* Invitation acceptance: unified onboarding while `kyc_onboarding` is ON, legacy InviteAccept otherwise. */}
              <Route path="/invite/:token" element={<InviteEntry />} />
              {/* Dev-only: Preview the animated property import screen */}
              {import.meta.env.DEV && (
                <Route path="/owner-import-demo" element={<OwnerImportDemo />} />
              )}
              
              {/* No-Access / Roleless fallback page */}
              <Route path="/no-access" element={<NoAccess />} />
              
              {/* Super Admin Portal */}
              <Route path="/super-admin/*" element={
                <RoleGate roles={[SystemRole.SuperAdmin]}>
                  <SuperAdminPortal />
                </RoleGate>
              } />
              
              {import.meta.env.DEV && <Route path="/test-hyperpay" element={<TestHyperPay />} />}
              
              {/* Public Invoice View — no auth required */}
              {/* Master must precede :id so "master" isn't captured as an invoice id. */}
              <Route path="/invoice/master/:id" element={<MasterInvoicePublicView />} />
              <Route path="/invoice/:id" element={<InvoicePublicView />} />
              <Route path="/quotation/:token" element={<QuotationPublicView />} />

              {/* Public Receipt View — no auth required, UUID is the access token */}
              <Route path="/receipt/:id" element={<ReceiptPublicView />} />

              {/* Public Task Report — no auth required, UUID is the access token */}
              <Route path="/task-report/:taskId" element={<TaskReportPublicView />} />

              {/* Invoice Payment Flow — public, no auth required */}
              <Route path="/pay/invoice/:id" element={<InvoiceCheckout />} />
              <Route path="/pay/invoice/:id/callback" element={<InvoicePaymentCallback />} />
              <Route path="/pay/master/:id" element={<MasterInvoiceCheckout />} />
              <Route path="/pay/master/:id/callback" element={<MasterInvoicePaymentCallback />} />

              {/* Admin Invite Flow */}
              <Route path="/admin-invite/*" element={<AdminInviteRoutes />} />
              
              {/* Portal Routes - Role-specific portals */}
              <Route
                path="/portal/owner/*"
                element={
                  <ProtectedRoute>
                    <RoleGate roles={["owner"]} redirectTo={portalPathForRole}>
                      <OwnerPortal />
                    </RoleGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/portal/tenant/*"
                element={
                  <ProtectedRoute>
                    <RoleGate roles={["tenant"]} redirectTo={portalPathForRole}>
                      <TenantPortal />
                    </RoleGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/portal/employee/*"
                element={
                  <ProtectedRoute>
                    <RoleGate roles={["employee"]} redirectTo={portalPathForRole}>
                      <EmployeePortal />
                    </RoleGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/portal/provider/*"
                element={
                  <ProtectedRoute>
                    <RoleGate
                      roles={["service_provider"]}
                      redirectTo={portalPathForRole}
                    >
                      <ServiceProviderPortal />
                    </RoleGate>
                  </ProtectedRoute>
                }
              />

              {/* Protected Routes - Admin/Manager Dashboard */}
              <Route
                path="/dashboard/*"
                element={
                  <ProtectedRoute>
                    <RoleGate
                      roles={["super_admin", "admin", "manager"]}
                      redirectTo={portalPathForRole}
                    >
                      <AppLayout>
                        <Routes>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/requests" element={<PermissionGuard permission="service_requests.view"><RealEstateRequests /></PermissionGuard>} />
                        <Route path="/requests/:id" element={<PermissionGuard permission="service_requests.view"><RealEstateRequestDetails /></PermissionGuard>} />
                        <Route path="/service-requests" element={<PermissionGuard permission="service_requests.view"><MasterCardsList /></PermissionGuard>} />
                        <Route path="/service-requests/:id" element={<PermissionGuard permission="service_requests.view"><MasterCardDetail /></PermissionGuard>} />
                        <Route path="/service-requests/:id/units/:unitId" element={<PermissionGuard permission="service_requests.view"><MasterCardUnitDetail /></PermissionGuard>} />
                        {/* Properties */}
                        <Route path="/properties" element={<PermissionGuard permission="properties.view"><Properties /></PermissionGuard>} />
                        <Route path="/properties/:id" element={<PermissionGuard permission="properties.view"><PropertyDetails /></PermissionGuard>} />
                        <Route path="/properties/:id/units/:unitId" element={<PermissionGuard permission="units.view"><UnitDetails /></PermissionGuard>} />
                        {/* Advertisements */}
                        <Route path="/ads" element={<PermissionGuard permission="advertisements.view"><Ads /></PermissionGuard>} />
                        <Route path="/ads/:id" element={<PermissionGuard permission="advertisements.view"><AdDetails /></PermissionGuard>} />

                        {/* Opportunities (off-plan projects) — the SAME pages the owner
                            portal mounts; staff capabilities come from `projects.*`.
                            Demand/leads live in the CRM address book (/dashboard/leads). */}
                        <Route path="/opportunities" element={<PermissionGuard permission="projects.view"><Opportunities /></PermissionGuard>} />
                        <Route path="/opportunities/:id" element={<PermissionGuard permission="projects.view"><OpportunityDetails /></PermissionGuard>} />
                        <Route path="/opportunities/:id/units/:unitId" element={<PermissionGuard permission="projects.view"><OpportunityUnitDetails /></PermissionGuard>} />
                        {/* Legacy admin path (pre-unification) */}
                        <Route path="/projects" element={<Navigate to="/dashboard/opportunities" replace />} />
                        {/* Contracts */}
                        <Route path="/contracts" element={<PermissionGuard permission="contracts.view"><Contracts /></PermissionGuard>} />
                        <Route path="/contracts/:id" element={<PermissionGuard permission="contracts.view"><ContractDetails /></PermissionGuard>} />
                        <Route path="/contract-requests" element={<PermissionGuard permission="contracts.view"><ContractRequests /></PermissionGuard>} />
                        {/* S9 · [L] Address book — the CEO's leads view (reuses the service_requests demand surface) */}
                        <Route path="/leads" element={<PermissionGuard permission="service_requests.view"><LeadsAddressBook /></PermissionGuard>} />
                        <Route path="/leads/:id" element={<PermissionGuard permission="service_requests.view"><LeadDetails /></PermissionGuard>} />
                        {/* Brokerage contracts (Inc-2: wizard host; Inc-3: list/detail/edit) */}
                        <Route path="/brokerage" element={<PermissionGuard permission="contracts.view"><BrokerageContractsList /></PermissionGuard>} />
                        <Route path="/brokerage/new" element={<PermissionGuard permission="contracts.create"><NewBrokerageContract /></PermissionGuard>} />
                        <Route path="/brokerage/:id" element={<PermissionGuard permission="contracts.view"><BrokerageContractDetail /></PermissionGuard>} />
                        <Route path="/brokerage/:id/edit" element={<PermissionGuard permission="contracts.edit"><EditBrokerageContract /></PermissionGuard>} />
                        {/* Finance Routes */}
                        <Route path="/finance" element={<PermissionGuard permission="finance.view"><Finance /></PermissionGuard>} />
                        <Route path="/finance/invoices/:id" element={<PermissionGuard permission="finance.view"><InvoiceDetails /></PermissionGuard>} />
                        <Route path="/finance/receipts/:id" element={<PermissionGuard permission="finance.view"><ReceiptDetails /></PermissionGuard>} />
                        <Route path="/finance/expenses/:id" element={<PermissionGuard permission="finance.view"><ExpenseDetails /></PermissionGuard>} />
                        <Route path="/finance/payment-verifications" element={<PermissionGuard permission="finance.edit"><PaymentVerifications /></PermissionGuard>} />
                        {/* Reports Routes */}
                        <Route path="/reports" element={<PermissionGuard permission="reports.view"><Reports /></PermissionGuard>} />
                        <Route path="/reports/financials" element={<PermissionGuard permission="reports.view"><FinancialReports /></PermissionGuard>} />
                        <Route path="/reports/revenue" element={<PermissionGuard permission="reports.view"><RevenueReport /></PermissionGuard>} />
                        <Route path="/reports/collections" element={<PermissionGuard permission="reports.view"><CollectionsReport /></PermissionGuard>} />
                        <Route path="/reports/operating-expenses" element={<PermissionGuard permission="reports.view"><OperatingExpensesReport /></PermissionGuard>} />
                        <Route path="/reports/maintenance" element={<PermissionGuard permission="reports.view"><MaintenanceReports /></PermissionGuard>} />
                        <Route path="/reports/accounts-receivable" element={<PermissionGuard permission="reports.view"><AccountsReceivableReport /></PermissionGuard>} />
                        <Route path="/reports/property-performance" element={<PermissionGuard permission="reports.view"><PropertyPerformanceReport /></PermissionGuard>} />
                        <Route path="/reports/occupancy" element={<PermissionGuard permission="reports.view"><OccupancyReports /></PermissionGuard>} />
                        <Route path="/reports/invoices" element={<PermissionGuard permission="reports.view"><InvoiceReport /></PermissionGuard>} />
                        <Route path="/reports/income-statement" element={<PermissionGuard permission="reports.view"><IncomeStatementReport /></PermissionGuard>} />
                        <Route path="/reports/tenants" element={<PermissionGuard permission="reports.view"><TenantsReport /></PermissionGuard>} />
                        <Route path="/reports/balance-sheet" element={<PermissionGuard permission="reports.view"><BalanceSheetReport /></PermissionGuard>} />
                        <Route path="/reports/cash-flow" element={<PermissionGuard permission="reports.view"><CashFlowReport /></PermissionGuard>} />
                        {/* Profile & Settings — no permission guard (user's own profile) */}
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/settings" element={<PermissionGuard permission="settings.view"><Settings /></PermissionGuard>} />
                        <Route path="/my-subscription" element={<MySubscription />} />
                        <Route path="/rer-verifications" element={<PermissionGuard permission="properties.view"><RerVerifications /></PermissionGuard>} />
                        <Route path="/rega-compliance" element={<PermissionGuard permission="properties.view"><RegaDashboard /></PermissionGuard>} />
                        <Route path="/notifications" element={<NotificationsCenter />} />
                        {/* WhatsApp messaging — open conversation by recipient phone (E.164, URL-encoded) */}
                        <Route path="/conversations" element={<ConversationsPage />} />
                        <Route path="/messages/:phone" element={<MessagesPage />} />
                        {/* Tenants - detail page only */}
                        <Route path="/tenants/:id" element={<PermissionGuard permission="tenants.view"><TenantDetails /></PermissionGuard>} />
                        {/* Owners - detail page only */}
                        <Route path="/owners/:id" element={<PermissionGuard permission="owners.view"><OwnerDetails /></PermissionGuard>} />
                        {/* Employees */}
                        <Route path="/employees" element={<PermissionGuard permission="employees.view"><Employees /></PermissionGuard>} />
                        <Route path="/employees/:id" element={<PermissionGuard permission="employees.view"><EmployeeDetails /></PermissionGuard>} />
                        {/* Referrals report (admin oversight of employee referral links) */}
                        <Route path="/referrals" element={<PermissionGuard permission="employees.view"><ReferralsReport /></PermissionGuard>} />
                        {/* Any dashboard user (admins included) can refer owners — their own link + earnings. */}
                        <Route path="/my-referrals" element={<MyReferrals />} />
                        {/* Media review (PR 4b) — the special media.approve grant; admins pass */}
                        <Route path="/media-review" element={<PermissionGuard permission="media.approve"><MediaReview /></PermissionGuard>} />
                        <Route path="/media-review/:sessionId" element={<PermissionGuard permission="media.approve"><MediaReview /></PermissionGuard>} />
                        {/* KYC compliance (PR 6b) — the EXPLICIT compliance.review grant; admins do NOT pass (KycComplianceGate in the page) */}
                        <Route path="/compliance/kyc" element={<KycCompliance />} />
                        <Route path="/compliance/kyc/:profileId" element={<KycCompliance />} />
                        {/* Maintenance */}
                        <Route path="/maintenance" element={<PermissionGuard permission="maintenance.view"><Maintenance /></PermissionGuard>} />
                        <Route path="/maintenance/:id" element={<PermissionGuard permission="maintenance.view"><MaintenanceRequestDetails /></PermissionGuard>} />
                        {/* Catch-all for dashboard routes */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </AppLayout>
                    </RoleGate>
                  </ProtectedRoute>
                }
              />

              {/* Legacy Django route redirects */}
              <Route path="/ar/*" element={<LegacyPrefixRedirect prefix="/ar" />} />
              <Route path="/en/*" element={<LegacyPrefixRedirect prefix="/en" />} />
              <Route path="/v4/*" element={<LegacyPrefixRedirect prefix="/v4" />} />

              {/* Known missing page redirects */}
              <Route path="/login" element={<Navigate to="/auth" replace />} />
              <Route path="/contact" element={<Navigate to="/request-service" replace />} />
              <Route path="/help" element={<Navigate to="/our-services" replace />} />
              <Route path="/support" element={<Navigate to="/our-services" replace />} />
              <Route path="/features/listings" element={<Navigate to="/advertisements" replace />} />
              <Route path="/properties" element={<Navigate to="/advertisements" replace />} />
              <Route path="/careers" element={<Navigate to="/about-us" replace />} />
              <Route path="/referral-program" element={<Navigate to="/" replace />} />
              <Route path="/faq" element={<Navigate to="/our-services" replace />} />

              {/* Catch-all 404 (public, noindex) */}
              <Route path="*" element={<LandingNotFound />} />
            </SentryRoutes>
              </Suspense>
                </TooltipProvider>
                </ImpersonationContentWrapper>
                </DemoProvider>
              </ImpersonationProvider>
            </AuthProvider>
            </GuideProvider>
          </LanguageProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </ErrorBoundary>
);

export default App;
